import { useEffect, useState } from 'react';
import { AtfCard } from './AtfCard';
import { useAcompanhamento } from './useAcompanhamento';
import { FILTROS_PERIODO, FILTROS_STATUS } from './atfStatusInfo';
import { Carregando, EstadoVazio } from '../../../common/interface';

const LIMITE_VISIVEL_INICIAL = 10;
/** Espera antes de consultar o servidor a cada tecla digitada. */
const DEBOUNCE_BUSCA_MS = 350;

interface Props {
  onEmitir?: () => void;
}

/**
 * Tela de Acompanhamento, no desenho de 03/09 (specs/0035 design/ §1-2).
 *
 * Período e busca são resolvidos no SERVIDOR: a página é de 30 e há cooperado
 * com 88 ATFs, então filtrar o que já foi carregado acharia menos do que
 * existe — sem dizer que achou.
 */
export function AcompanharLista({ onEmitir }: Props) {
  const {
    itens, total, carregando, atualizando, atualizadoEm, erro,
    filtros, definirFiltro, tetoAtingido, rodada, totalRodadas, atualizarManual, acompanhando,
  } = useAcompanhamento();
  const [expandidoId, setExpandidoId] = useState<number | null>(null);
  const [mostrarTodas, setMostrarTodas] = useState(false);
  const [buscaDigitada, setBuscaDigitada] = useState(filtros.busca);

  // Debounce: sem ele, cada tecla vira uma consulta ao banco.
  useEffect(() => {
    if (buscaDigitada === filtros.busca) return;
    const t = setTimeout(() => definirFiltro({ busca: buscaDigitada }), DEBOUNCE_BUSCA_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaDigitada]);

  const visiveis = mostrarTodas ? itens : itens.slice(0, LIMITE_VISIVEL_INICIAL);
  const hora = atualizadoEm?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <div className="co-cabecalho">
        <div>
          <h1 className="co-titulo-tela">Minhas ATFs</h1>
          <div className="co-faixa-texto">
            {acompanhando && <span className="co-pulso" aria-hidden="true" />}
            <p className="co-apoio" role="status">
              {acompanhando
                ? `Atualizando a cada 10 s enquanto houver ATF em andamento · rodada ${rodada} de ${totalRodadas}`
                : hora
                  ? `Atualizado às ${hora}`
                  : 'Carregando…'}
            </p>
          </div>
        </div>

        {onEmitir && (
          <button type="button" className="co-btn co-btn-primario" onClick={onEmitir}>
            Emitir ATF
          </button>
        )}
      </div>

      <div className="co-cartao co-filtros">
        <div className="co-filtros-grupo" role="group" aria-labelledby="co-filtro-periodo">
          <span id="co-filtro-periodo" className="co-rotulo">Período</span>
          {FILTROS_PERIODO.map(f => (
            <Pilula key={f.valor} ativa={filtros.periodo === f.valor} onClick={() => definirFiltro({ periodo: f.valor })}>
              {f.rotulo}
            </Pilula>
          ))}
        </div>
        <div className="co-filtros-grupo" role="group" aria-labelledby="co-filtro-status">
          <span id="co-filtro-status" className="co-rotulo">Status</span>
          {FILTROS_STATUS.map(f => (
            <Pilula key={f.valor} ativa={filtros.status === f.valor} onClick={() => definirFiltro({ status: f.valor })}>
              {f.rotulo}
            </Pilula>
          ))}
        </div>

        <div className="co-filtros-busca">
          <input
            className="co-entrada"
            type="search"
            aria-label="Buscar por número, placa ou destino"
            value={buscaDigitada}
            onChange={e => setBuscaDigitada(e.target.value)}
            placeholder="Buscar por número, placa ou destino"
          />
          {/* O contador diz que a tela está viva; o botão continua existindo
              porque o polling PARA no teto — e é ele que recarrega depois de
              cancelar ou excluir uma ATF. */}
          <button
            type="button"
            className={`co-btn ${tetoAtingido ? 'co-btn-primario' : 'co-btn-secundario'}`}
            onClick={atualizarManual}
            disabled={atualizando}
          >
            {atualizando ? 'Atualizando…' : tetoAtingido ? 'Retomar atualização' : 'Atualizar'}
          </button>
        </div>
      </div>

      <div className="co-contagem">
        <strong className="co-numero">{total} autorizaç{total === 1 ? 'ão' : 'ões'}</strong>
        <span>Mais recentes primeiro</span>
      </div>

      {tetoAtingido && (
        <p className="co-nota co-nota-atencao" role="status">
          A atualização automática parou depois de {totalRodadas} rodadas. Toque em "Retomar atualização" para continuar acompanhando.
        </p>
      )}

      {erro && <p className="co-erro" role="alert">{erro}</p>}

      {carregando && itens.length === 0 && !erro && <Carregando rotulo="Carregando as ATFs" />}

      {!carregando && itens.length === 0 && !erro && (
        <EstadoVazio
          titulo="Nenhuma ATF neste filtro"
          acao={(
            <button
              type="button"
              className="co-btn co-btn-secundario"
              onClick={() => { setBuscaDigitada(''); definirFiltro({ status: 'TODOS', periodo: 'ANO', busca: '' }); }}
            >
              Limpar filtros
            </button>
          )}
        >
          Mude o período ou o status para ver outras autorizações.
        </EstadoVazio>
      )}

      {visiveis.map(item => (
        <AtfCard
          key={item.id}
          item={item}
          expandido={expandidoId === item.id}
          onToggle={() => setExpandidoId(expandidoId === item.id ? null : item.id)}
          onMudou={atualizarManual}
        />
      ))}

      {!mostrarTodas && itens.length > LIMITE_VISIVEL_INICIAL && (
        <button type="button" className="co-mais" onClick={() => setMostrarTodas(true)}>
          Ver todas ({itens.length})
        </button>
      )}
    </div>
  );
}

function Pilula({ ativa, onClick, children }: { ativa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className={`fc-chip${ativa ? ' fc-chip-ativo' : ''}`} aria-pressed={ativa} onClick={onClick}>
      {children}
    </button>
  );
}
